import React from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Stack,
  Alert,
  alpha,
} from '@mui/material';
import { useAppleMusicSongs, AppleMusicSongDetail } from 'src/hooks/useAppleMusicSongs';

interface AppleMusicSongListProps {
  /** Song IDs selected by the client for this particular field */
  songIds: string[];
  /** All song IDs across all playlist fields (used for shared fetch) */
  allSongIds: string[];
}

/** Format duration in ms to m:ss */
const fmtDuration = (ms: number | null | undefined): string => {
  if (!ms) return '';
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const AppleMusicSongList: React.FC<AppleMusicSongListProps> = ({ songIds, allSongIds }) => {
  const { songs, isLoading, error } = useAppleMusicSongs(allSongIds);

  if (isLoading) {
    return (
      <Stack spacing={1}>
        {songIds.slice(0, 3).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: 1 }} />
        ))}
        {songIds.length > 3 && (
          <Typography variant="caption" color="text.secondary">
            Loading {songIds.length} tracks…
          </Typography>
        )}
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
        Could not load track details — {songIds.length} song{songIds.length !== 1 ? 's' : ''} selected
      </Alert>
    );
  }

  // Map fetched songs by id for quick lookup
  const songMap = new Map<string, AppleMusicSongDetail>();
  songs.forEach((s) => songMap.set(s.id, s));

  // Filter to only the songs for this field, preserving selection order
  const fieldSongs = songIds
    .map((id) => songMap.get(id))
    .filter((s): s is AppleMusicSongDetail => !!s);

  if (fieldSongs.length === 0 && songIds.length > 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {songIds.length} song{songIds.length !== 1 ? 's' : ''} selected (details unavailable)
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
        {fieldSongs.length} song{fieldSongs.length !== 1 ? 's' : ''} selected
      </Typography>
      {fieldSongs.map((song, idx) => (
        <Box
          key={song.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 0.75,
            borderBottom: idx < fieldSongs.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
            '@media print': { pageBreakInside: 'avoid' },
          }}
        >
          {/* Artwork */}
          {song.artwork?.url && (
            <Box
              component="img"
              src={song.artwork.url}
              alt={song.name}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 0.5,
                flexShrink: 0,
                objectFit: 'cover',
              }}
            />
          )}

          {/* Primary info: track name + artist */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {song.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {song.artistName}
              {song.albumName && <> &middot; {song.albumName}</>}
            </Typography>
          </Box>

          {/* Secondary info: duration, label, ISRC */}
          <Box
            sx={{
              flexShrink: 0,
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
              {fmtDuration(song.durationMs)}
            </Typography>
            {song.recordLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '0.65rem',
                  lineHeight: 1.2,
                  maxWidth: 140,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {song.recordLabel}
              </Typography>
            )}
            {song.isrc && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  lineHeight: 1.2,
                  color: (theme) => alpha(theme.palette.text.secondary, 0.6),
                  fontFamily: 'monospace',
                }}
              >
                {song.isrc}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

export default AppleMusicSongList;
